import type { ForecastContextSelectedOptions } from "@appretio/context-selector";
import { getContextSyncStatus, type ScalarField } from "@appretio/shared";
import { useMemo } from "react";
import { FORECAST_CONTEXT_FIELDS } from "../forecastContext";
import { mergeStoreLocations } from "../utils/mergeStoreLocations";
import { useFetchStoreStatusOptions } from "./useFetchStoreStatusOptions";
import { useForecastBaseContextOptions } from "./useForecastBaseContextOptions";
import { useForecastFilteredContextOptions } from "./useForecastFilteredContextOptions";
import { useStoreStatusContextMerge } from "./useStoreStatusContextMerge";

/** Options for {@link useContextOptions}. */
interface UseContextOptionsOptions {
  /** Cube IDs to pass to the context options queries. */
  cubeIds: string[];
  /** Current context filter selections from the UI. */
  selectedContextOptions: ForecastContextSelectedOptions;
  /** Currently selected store status values. */
  selectedStoreStatuses: string[];
}

/**
 * Fetches context options and resolves store-status cross-filtering.
 *
 * Carved out of the old ``useContextWithSync`` so pages with a circular
 * dependency between "applied context" and "resolved store locations"
 * (where a workspace needs ``resolvedStoreLocations`` to build
 * ``appliedFilters``, and the sync check needs ``appliedFilters``) can
 * compute the options first, run the workspace, and then compute the
 * sync check synchronously — no useEffect mirror needed.
 */
export function useContextOptions({
  cubeIds,
  selectedContextOptions,
  selectedStoreStatuses
}: UseContextOptionsOptions) {
  const { data: baseContextOptions, isLoading: baseLoading } =
    useForecastBaseContextOptions({ cubeIds, enabled: true });

  const hasActiveFilters = Object.values(selectedContextOptions ?? {}).some(
    (v) => v && v.length > 0
  );

  const { data: filteredContextOptions, isLoading: filteredLoading } =
    useForecastFilteredContextOptions({
      fields: FORECAST_CONTEXT_FIELDS,
      cubeIds,
      filters: selectedContextOptions,
      enabled: true
    });

  const { data: storeStatusOptions, isLoading: storeStatusLoading } =
    useFetchStoreStatusOptions();

  const {
    resolvedStoreLocations,
    mergedFilteredContextOptions,
    relevantStoreStatuses
  } = useStoreStatusContextMerge(
    selectedStoreStatuses,
    (selectedContextOptions as Record<string, string[] | undefined>)
      ?.sales_location,
    storeStatusOptions,
    filteredContextOptions as Record<string, string[]> | undefined
  );

  const currentContextForSync = useMemo(
    () =>
      mergeStoreLocations(
        (selectedContextOptions ?? {}) as Record<string, string[] | undefined>,
        resolvedStoreLocations
      ),
    [selectedContextOptions, resolvedStoreLocations]
  );

  return {
    baseContextOptions,
    baseLoading,
    filteredLoading,
    hasActiveFilters,
    mergedFilteredContextOptions,
    relevantStoreStatuses,
    resolvedStoreLocations,
    storeStatusOptions,
    storeStatusLoading,
    currentContextForSync
  };
}

/** Options for {@link useContextSyncCheck}. */
interface UseContextSyncCheckOptions {
  /** Base context options (used to detect "all options selected" staleness). */
  baseContextOptions: Record<string, string[]> | undefined;
  /** Current context values, already merged with resolved store locations. */
  currentContextForSync: Record<string, string[] | undefined>;
  /** The applied-at-Apply context snapshot. ``undefined`` before first Apply. */
  appliedContext: Record<string, string[] | undefined> | undefined;
  /** Additional non-context fields to include in the sync check. */
  extraSyncFields?: ScalarField[];
}

/**
 * Detects whether the current UI selection has drifted from the last
 * applied snapshot. Split from {@link useContextOptions} so callers whose
 * ``appliedContext`` is derived from a hook that itself depends on the
 * options (e.g. a workspace using ``resolvedStoreLocations``) can feed
 * the latest value in synchronously — without a useEffect mirror that
 * introduces a one-render-lag.
 */
export function useContextSyncCheck({
  baseContextOptions,
  currentContextForSync,
  appliedContext,
  extraSyncFields
}: UseContextSyncCheckOptions) {
  const { isDirty: isContextDirty, changedFields } = getContextSyncStatus(
    FORECAST_CONTEXT_FIELDS.map((field) => ({
      name: field,
      current: currentContextForSync[field],
      applied: appliedContext?.[field],
      allOptions: baseContextOptions?.[field]
    })),
    extraSyncFields ?? []
  );
  return { isContextDirty, changedFields };
}

/** Options for {@link useContextWithSync}. */
interface UseContextWithSyncOptions extends UseContextOptionsOptions {
  /** Applied context snapshot (undefined before first apply). */
  appliedContext: Record<string, string[] | undefined> | undefined;
  /** Additional non-context fields to include in the sync check. */
  extraSyncFields?: ScalarField[];
}

/**
 * Shared hook for context option fetching, store-status cross-filtering,
 * and context sync detection. Used by the summary page, which owns its
 * ``appliedFilters`` state directly and has no cycle to break.
 *
 * The main page avoids this composed form because ``appliedContext`` is
 * derived from a workspace that itself depends on ``resolvedStoreLocations``;
 * it uses {@link useContextOptions} and {@link useContextSyncCheck}
 * separately to thread the workspace's applied-filters in synchronously.
 */
export function useContextWithSync({
  cubeIds,
  selectedContextOptions,
  selectedStoreStatuses,
  appliedContext,
  extraSyncFields
}: UseContextWithSyncOptions) {
  const options = useContextOptions({
    cubeIds,
    selectedContextOptions,
    selectedStoreStatuses
  });
  const sync = useContextSyncCheck({
    baseContextOptions: options.baseContextOptions as
      | Record<string, string[]>
      | undefined,
    currentContextForSync: options.currentContextForSync,
    appliedContext,
    extraSyncFields
  });
  return { ...options, ...sync };
}
